import { t } from "../i18n";
import { pimg } from "../art/cast";
import { $, h } from "../lib/dom";
import { resolve } from "../lib/util";
import { syncDrawer } from "./drawer";
import { appendLog } from "./log";
import { L, rt, S } from "./state";
import { renderHUD } from "./time";
import type { Choice, ChoiceItem, Dialog } from "./types";
import { onDialogOpened } from "./waits";
import { closeZoom, renderZBar, zoomDialog } from "./zoom";

/** Choice button shorthand used by every level. */
export const C = (label: string, fn: () => void, dis?: boolean, cls?: string): Choice => ({ label, fn, dis, cls });

export const currentDialog = (): Dialog | undefined => (S.dialog ? L.dialogs[S.dialog] : undefined);

export function openDialog(id: string): void {
  const D = L.dialogs[id];
  if (!D) return;
  if (S.ended && !D.doc) return;
  if (D.onOpen?.() === false) return;
  S.dialog = id;
  rt.fastForward = false;
  onDialogOpened(id);
  renderSide();
}

export function closeDialog(): void {
  S.dialog = null;
  if (zoomDialog()) closeZoom();
  renderSide();
}

/** Re-render the side panel: either the full field log or the open dialog. */
export function renderSide(): void {
  const head = $("#sideHead");
  const log = $("#log");
  head.replaceChildren();
  log.replaceChildren();
  syncDrawer();
  const D = currentDialog();
  if (!D) {
    head.appendChild(titleBlock(t("side.fieldLog"), t("side.fieldHint")));
    S.log.forEach((e) => appendLog(e, false));
    $("#choices").replaceChildren();
    $("#composer").hidden = true;
    return;
  }
  const plist = resolve(D.pics ?? []);
  const pics = h("div", { className: "pics" + (plist.length > 3 ? " many" : "") }, ...plist.map(pimg));
  const leave = h("button", { className: "btn", text: t("side.leave") });
  leave.onclick = closeDialog;
  head.append(pics, titleBlock(resolve(D.title), resolve(D.sub ?? "")), leave);
  if (D.doc) D.doc(log);
  else S.log.filter((e) => e.ch === D.ch).forEach((e) => appendLog(e, false));
  log.scrollTop = log.scrollHeight;
  updateControls();
}

function titleBlock(title: string, sub: string): HTMLElement {
  return h("div", { className: "ttl" }, h("b", { text: title }), h("span", { text: sub }));
}

/** Controls are inert while waiting on the model, after the level ends, or in cutscenes. */
export const controlsLocked = (): boolean => S.busy || S.ended || S.cine;

export function makeBtn(it: Choice, after?: () => void): HTMLButtonElement {
  const b = h("button", { className: "btn" + (it.cls ? ` ${it.cls}` : ""), text: it.label });
  b.disabled = !!it.dis || controlsLocked();
  const hold = it.hold;
  if (hold) {
    const on = (e: PointerEvent): void => {
      e.preventDefault();
      if (S.lap) S.lap.pan = hold;
    };
    const off = (): void => {
      if (S.lap) S.lap.pan = null;
    };
    b.addEventListener("pointerdown", on);
    for (const ev of ["pointerup", "pointerleave", "pointercancel"] as const) b.addEventListener(ev, off);
    b.onclick = () => {
      if (S.lap) {
        S.lap.cx += (hold[0] * 10) / S.lap.z;
        S.lap.cy += (hold[1] * 10) / S.lap.z;
      }
    };
  } else
    b.onclick = () => {
      rt.fastForward = false;
      it.fn();
      after?.();
    };
  return b;
}

function renderChoice(it: ChoiceItem): HTMLElement {
  if (it === "sep") return h("span", { className: "sep" });
  if (typeof it === "string") return h("span", { className: "lbl", text: it });
  return makeBtn(it, () => {
    if (S.live && S.dialog) updateControls();
    renderZBar();
    renderHUD();
  });
}

/** Refresh the choice buttons and composer state of the open dialog. */
export function updateControls(): void {
  if (!S.live) return;
  const D = currentDialog();
  if (!D) return;
  $("#choices").replaceChildren(...(D.choices?.() ?? []).map(renderChoice));
  $("#composer").hidden = !resolve(D.talk ?? false);
  const off = controlsLocked() || S.llmOff;
  const input = $<HTMLTextAreaElement>("#input");
  input.disabled = off;
  $<HTMLButtonElement>("#sendBtn").disabled = off;
  input.placeholder = resolve(D.ph ?? "") || t("side.placeholderShort");
}

/** Dialog renderer for read-only documents (charts, printouts): a definition list. */
export function doc(items: () => [term: string, detail: string][]): (log: HTMLElement) => void {
  return (log) => {
    const dl = h("dl", { className: "doc" });
    for (const [a, b] of items()) dl.append(h("dt", { text: a }), h("dd", { text: b }));
    log.appendChild(dl);
  };
}

/** Record an exam / test finding for the debrief. */
export function finding(name: string, text: string): void {
  S.findings.push({ t: S.t, name, text });
}

/** Put the caret in the chat box (e.g. after clicking a patient's face in a close-up). */
export const focusInput = (): void => $<HTMLTextAreaElement>("#input").focus();
